// buildSnapshotReport — pure report-building logic extracted from
// CustomerSnapshot.jsx so the same report can be generated for any client
// without opening their dashboard (used by the bulk monthly send).
// The processing/HTML here must stay identical to what the single-client
// snapshot produces — CustomerSnapshot.jsx delegates to these functions.
import { renderToStaticMarkup } from "react-dom/server";
import { getFundClass } from "./reportComputations";
import { computeHonKitzba } from "./honKitzba";
import CustomerSnapshotReport from "../components/CustomerSnapshotReport";

export const EMPTY_SNAPSHOT = { globalComment: "", fundComments: {} };

const PRODUCT_ORDER = [
  "קרן פנסיה", "ביטוח מנהלים", "קרן השתלמות",
  "קופת גמל", "קופת גמל להשקעה", "ביטוח חיים", "חיסכון לכל ילד"
];

export function processSnapshotFunds(funds) {
  return funds.map(f => {
    const type = getFundClass(f);
    const redeem = f.redeem || 0;

    // Last deposit
    const lastDep = f.lastDeposit?.[0];
    const lastDepAmt = lastDep?.total || 0;
    const lastDepDate = lastDep?.date || "—";
    const lastDepEmployer = lastDep?.breakdown?.[0]?.maasikName || f.employer?.name || "—";

    // Fees
    const dmeiTzv = f.dmeiNihul?.actual?.dmeiTzvPct || 0;
    const dmeiHaf = f.dmeiNihul?.actual?.dmeiHafPct || 0;

    // Tracks grouped by name with summed balances
    const groupedMaslulim = Object.values(
      (f.maslulim || []).reduce((acc, m) => {
        const key = m.name || "—";
        if (!acc[key]) acc[key] = { name: key, balance: m.balance || 0 };
        else acc[key].balance += m.balance || 0;
        return acc;
      }, {})
    );

    // Detailed split
    const hkRaw = computeHonKitzba(f);

    const isLoMeshalemet = type === "קצבה לא משלמת";

    // פירוט רכיבים כמו בטבלאות — בלי פיצול משלמת/לא משלמת, עם רכיב "עד 2000" נפרד
    const hk = {
      hon:       hkRaw.hon,
      kitzba:    Math.max(0, (hkRaw.kitzba || 0) - (hkRaw.kitzbaPreSettlement || 0)),
      until2000: (hkRaw.kitzbaPreSettlement || 0) + (hkRaw.preReform || 0),
      honP:      hkRaw.honPitzuyim,
      kitzbaP:   hkRaw.kitzbaPitzuyim,
      totalP:    hkRaw.honPitzuyim + hkRaw.kitzbaPitzuyim,
    };

    // Pension
    const pension = f.retirementProjections?.[0]?.kupot?.[0]?.pensionMonthly
                 || f.retirementProjections?.[0]?.kupot?.[0]?.pension || 0;

    // Coverages
    const lifeInsurance = f.kisuim?.reduce((max, k) => Math.max(max, k.bituachLemavet || 0), 0) || 0;
    const disability = f.kisuim?.reduce((max, k) => Math.max(max, k.kitzbatNechut || k.sheurNechut ? (k.sheurNechut * (f.employment?.salary || 0) / 100) : 0), 0) || 0;

    const currentYear = new Date().getFullYear().toString();
    const isDepositFromCurrentYear = lastDepDate && lastDepDate.includes(currentYear);

    return {
      id: f.id,
      company: f.companyName,
      product: f.cat,
      tochnit: f.tochnit,
      polNum: f.polNum,
      type,
      hk, // detailed breakdown
      redeem,
      groupedMaslulim,
      dmeiTzv,
      dmeiHaf,
      lastDepAmt: isDepositFromCurrentYear ? lastDepAmt : 0,
      lastDepDate: isDepositFromCurrentYear ? lastDepDate : "—",
      lastDepEmployer: isDepositFromCurrentYear ? lastDepEmployer : "—",
      pension,
      mekadem: f.retirementProjections?.[0]?.mekadem || 0,
      mutavim: f.mutavim || [],
      lifeInsurance,
      disability,
      isFrozen: (function() {
        if (!lastDepDate || lastDepDate === "—") return false;
        const [d, m, y] = lastDepDate.split("/");
        const lastDate = new Date(y, m - 1, d);
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        return lastDate < threeMonthsAgo;
      })(),
      statusLabel:  f.statusLabel  || "—",
      statusCode:   f.statusCode   || "",
      employerName: f.employer?.name || "—",
      isMeshalemet: !isLoMeshalemet,
      validDate:    f.validDate || "",
    };
  }).sort((a, b) => {
    const ai = PRODUCT_ORDER.indexOf(a.product);
    const bi = PRODUCT_ORDER.indexOf(b.product);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function computeSnapshotTotals(processedFunds) {
  return {
    redeem: processedFunds.reduce((s, f) => s + f.redeem, 0),
    pension: processedFunds.reduce((s, f) => s + f.pension, 0),
    life: processedFunds.reduce((s, f) => s + f.lifeInsurance, 0),
  };
}

// Full standalone HTML of the report — used for both the emailed PDF and
// the PDF preview. Heebo + dir=rtl are required for correct Hebrew rendering
// in Puppeteer on the server.
export function buildSnapshotReportHtml({
  funds, clientName, logo,
  snapshot = EMPTY_SNAPSHOT, hideRecommendations = true,
  processedFunds = null, totals = null,
}) {
  const pf = processedFunds || processSnapshotFunds(funds);
  const t  = totals || computeSnapshotTotals(pf);
  const bodyHtml = renderToStaticMarkup(
    <CustomerSnapshotReport
      funds={pf} snapshot={snapshot} clientName={clientName}
      logo={logo} totals={t} hideRecommendations={hideRecommendations}
    />
  );
  return `<!DOCTYPE html><html lang="he" dir="rtl">
      <head>
        <meta charset="utf-8" />
        <title>דוח מצב פנסיוני — ${clientName}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link href="https://fonts.googleapis.com/css2?family=Heebo:wght@400;600;700;800&display=swap" rel="stylesheet" />
        <style>body, body * { font-family: 'Heebo', 'Segoe UI', Arial, sans-serif !important; }</style>
      </head>
      <body style="margin:0;padding:20px;background:white">
        <div style="max-width:1000px;margin:0 auto">${bodyHtml}</div>
      </body></html>`;
}
